import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IChat, IMessage, IUser, IConnectionRequest } from '../interfaces/IChatUser';

interface ChatState {
  chats: IChat[];
  connectionRequests: IConnectionRequest[];
  unreadCounts: { [userId: string]: number };
  onlineStatus: { [userId: string]: boolean };
  connectionsVersion: number; // used to trigger refetches of accepted connections list
  isReconnecting: boolean; // SignalR reconnection state
}

const initialState: ChatState = {
  chats: [],
  connectionRequests: [],
  unreadCounts: {},
  onlineStatus: {},
  connectionsVersion: 0,
  isReconnecting: false,
};

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setChats(state, action: PayloadAction<IChat[]>) {
      state.chats = action.payload;
    },
    addChat(state, action: PayloadAction<IChat>) {
      state.chats.push(action.payload);
    },
    removeChat(state, action: PayloadAction<string>) {
      state.chats = state.chats.filter(chat => chat.id !== action.payload);
    },
    addMessage(state, action: PayloadAction<{ chatId: string; message: IMessage }>) {
      const chat = state.chats.find(c => c.id === action.payload.chatId);
      if (chat) {
        // Check if message already exists to prevent duplicates
        const messageExists = chat.messages.some(msg =>
          msg.senderId === action.payload.message.senderId &&
          msg.content === action.payload.message.content &&
          Math.abs(new Date(msg.timestamp).getTime() - new Date(action.payload.message.timestamp).getTime()) < 2000
        );

        if (!messageExists) {
          chat.messages.push(action.payload.message);
          chat.lastActivity = new Date().toISOString();
        }
      }
    },
    setIsTyping(state, action: PayloadAction<{ chatId: string; isTyping: boolean }>) {
      const chat = state.chats.find(c => c.id === action.payload.chatId);
      if (chat) {
        chat.isTyping = action.payload.isTyping;
      }
    },
    updateMessageStatus(
      state,
      action: PayloadAction<{ chatId: string; clientId: string; messageId?: string; status: IMessage['status'] }>
    ) {
      const chat = state.chats.find(c => c.id === action.payload.chatId);
      if (!chat) return;
      const message = chat.messages.find(m => m.clientId === action.payload.clientId);
      if (!message) return;
      message.status = action.payload.status;
      if (action.payload.messageId) {
        message.id = action.payload.messageId;
      }
      if (action.payload.status === 'read') {
        message.isRead = true;
      }
    },
    markMessagesAsRead(state, action: PayloadAction<{ chatId: string }>) {
      const chat = state.chats.find(c => c.id === action.payload.chatId);
      if (!chat) return;
      chat.messages.forEach(message => {
        if (message.status && message.status !== 'failed' && message.status !== 'sending') {
          message.status = 'read';
          message.isRead = true;
        }
      });
    },
    setUnreadCounts(state, action: PayloadAction<{ [userId: string]: number }>) {
      state.unreadCounts = action.payload;
    },
    setConnectionRequests(state, action: PayloadAction<IConnectionRequest[]>) {
      state.connectionRequests = action.payload;
    },
    addConnectionRequest(state, action: PayloadAction<IConnectionRequest>) {
      state.connectionRequests.push(action.payload);
    },
    removeConnectionRequest(state, action: PayloadAction<string>) {
      state.connectionRequests = state.connectionRequests.filter(r => r.id !== action.payload);
    },
    clearUnreadCount(state, action: PayloadAction<string>) {
      state.unreadCounts[action.payload] = 0;
    },
    incrementUnreadCount(state, action: PayloadAction<string>) {
      state.unreadCounts[action.payload] = (state.unreadCounts[action.payload] || 0) + 1;
    },
    setUserOnline(state, action: PayloadAction<string>) {
      state.onlineStatus[action.payload] = true;
    },
    setUserOffline(state, action: PayloadAction<string>) {
      state.onlineStatus[action.payload] = false;
    },
    setOnlineStatus(state, action: PayloadAction<{ [userId: string]: boolean }>) {
      state.onlineStatus = action.payload;
    },
    bumpConnectionsVersion(state) {
      state.connectionsVersion += 1;
    },
    setReconnecting(state, action: PayloadAction<boolean>) {
      state.isReconnecting = action.payload;
    },
    updateMessageReactions(
      state,
      action: PayloadAction<{
        chatId: string;
        messageId: string;
        reactions: { counts: Record<string, number>; userReaction?: string };
      }>
    ) {
      const chat = state.chats.find(c => c.id === action.payload.chatId);
      if (!chat) return;
      const message = chat.messages.find(m => m.id === action.payload.messageId);
      if (!message) return;
      message.reactions = action.payload.reactions;
    },
  },
});

export const {
  setChats,
  addChat,
  removeChat,
  addMessage,
  setIsTyping,
  updateMessageStatus,
  markMessagesAsRead,
  setUnreadCounts,
  setConnectionRequests,
  addConnectionRequest,
  removeConnectionRequest,
  clearUnreadCount,
  incrementUnreadCount,
  setUserOnline,
  setUserOffline,
  setOnlineStatus,
  bumpConnectionsVersion,
  setReconnecting,
  updateMessageReactions,
} = chatSlice.actions;

export default chatSlice.reducer;
